import React from "react";
import styled from "styled-components";
import { UploadResult } from "../../types/operations";

// =============================================
// Variant drives the banner colour scheme.
// =============================================
type BannerVariant = "success" | "partial" | "error";

interface BannerWrapperProps {
  $variant: BannerVariant;
}

const BACKGROUND_BY_VARIANT: Record<BannerVariant, string> = {
  success: "#e8f5e9",
  partial: "#fff8e1",
  error: "#fce4ec",
};

const BORDER_BY_VARIANT: Record<BannerVariant, string> = {
  success: "#a5d6a7",
  partial: "#ffe082",
  error: "#f48fb1",
};

const TEXT_BY_VARIANT: Record<BannerVariant, string> = {
  success: "#2e7d32",
  partial: "#f57f17",
  error: "#c62828",
};

const BannerWrapper = styled.div<BannerWrapperProps>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin: 12px 0;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid ${(props) => BORDER_BY_VARIANT[props.$variant]};
  background: ${(props) => BACKGROUND_BY_VARIANT[props.$variant]};
  color: ${(props) => TEXT_BY_VARIANT[props.$variant]};
  font-size: 13px;
  line-height: 1.5;
`;

const BannerContent = styled.div`
  flex: 1;
`;

const BannerTitle = styled.div`
  font-weight: 700;
  margin-bottom: 2px;
`;

const BannerDetail = styled.div`
  font-weight: 400;
  opacity: 0.9;
`;

const DismissButton = styled.button`
  flex-shrink: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  padding: 0 2px;
  color: inherit;
  opacity: 0.6;

  &:hover {
    opacity: 1;
  }
`;

// =============================================
// Props for the upload result banner.
// Accepts either a completed UploadResult or
// a raw error string, never both simultaneously.
// =============================================
interface UploadResultBannerProps {
  result: UploadResult | null;
  errorMessage: string | null;
  onDismiss: () => void;
}

function determineBannerVariant(result: UploadResult): BannerVariant {
  if (result.conflictingUploadKeys.length > 0) {
    return "error";
  }
  if (result.rowsRejected > 0) {
    return "partial";
  }
  return "success";
}

function buildResultTitle(result: UploadResult): string {
  if (result.conflictingUploadKeys.length > 0) {
    return "Upload blocked — already imported";
  }
  if (determineBannerVariant(result) === "success") {
    return "Upload successful";
  }
  return "Upload completed with warnings";
}

function buildResultDetail(result: UploadResult): string {
  if (result.conflictingUploadKeys.length > 0) {
    const descriptions = result.conflictingUploadKeys.map(
      (k) => `${k.machineType} on ${k.date}`
    );
    return `${result.fileName} — data for ${descriptions.join(", ")} was previously imported.`;
  }

  const parts: string[] = [];

  parts.push(`${result.rowsInserted} row(s) inserted`);

  if (result.rowsRejected > 0) {
    parts.push(`${result.rowsRejected} rejected (invalid or unknown machine)`);
  }

  return `${result.fileName} — ${parts.join(", ")}.`;
}

const UploadResultBanner: React.FC<UploadResultBannerProps> = ({
  result,
  errorMessage,
  onDismiss,
}) => {
  if (errorMessage) {
    return (
      <BannerWrapper $variant="error">
        <BannerContent>
          <BannerTitle>Upload failed</BannerTitle>
          <BannerDetail>{errorMessage}</BannerDetail>
        </BannerContent>
        <DismissButton onClick={onDismiss} aria-label="Dismiss">
          ✕
        </DismissButton>
      </BannerWrapper>
    );
  }

  if (!result) {
    return null;
  }

  const variant = determineBannerVariant(result);

  return (
    <BannerWrapper $variant={variant}>
      <BannerContent>
        <BannerTitle>{buildResultTitle(result)}</BannerTitle>
        <BannerDetail>{buildResultDetail(result)}</BannerDetail>
      </BannerContent>
      <DismissButton onClick={onDismiss} aria-label="Dismiss">
        ✕
      </DismissButton>
    </BannerWrapper>
  );
};

export default UploadResultBanner;
